<template>
    <div>
        <GarmentCustomOptions.define>
            <GarmentBodySelectorMenuInput
                v-if="isLoggedIn"
                v-model="bodyModel"
                :page-size="20"
                :id="product.uid"
            ></GarmentBodySelectorMenuInput>
            <template
                v-else
            >
                <GarmentGuestCustomOptions
                    v-model="garmentGuestOptsModel"
                    @update:model-value="onGuestBodyChange"
                ></GarmentGuestCustomOptions>

                <DividerWithText class="mt-2 mt-md-7 mb-5">OR</DividerWithText>

                <SignInPrompt>
                    Sign in to select a previously saved body
                </SignInPrompt>
            </template>
        </GarmentCustomOptions.define>
        <GarmentSizedOptions.define>
            <GarmentSizeOptionInput
                v-model="sizeModel"
                :options="product.garmentSizes ?? []"
            ></GarmentSizeOptionInput>
        </GarmentSizedOptions.define>

        <template
            v-if="!hasSizes"
        >
            <GarmentCustomOptions.reuse></GarmentCustomOptions.reuse>
        </template>
        <template
            v-else
        >
            <v-btn-toggle
                v-model="tabs"
                class="d-flex mb-6"
                variant="outlined"
                rounded="lg"
                divided
                mandatory
            >
                <v-btn
                    class="flex-basis-half"
                    :color="tabs === 0 ? 'primary' : 'accent'"
                    :value="0"
                >
                    Custom Fit
                </v-btn>
                <v-btn
                    class="flex-basis-half"
                    :color="tabs === 1 ? 'primary' : 'accent'"
                    :value="1"
                >
                    Standard Sizes
                </v-btn>
            </v-btn-toggle>
            <v-window
                v-model="tabs"
            >
                <v-window-item
                    :value="0"
                    class="pt-1"
                >
                    <GarmentCustomOptions.reuse></GarmentCustomOptions.reuse>
                </v-window-item>
                <v-window-item
                    :value="1"
                    class="pt-1"
                >
                    <GarmentSizedOptions.reuse></GarmentSizedOptions.reuse>
                    <div
                        class="text-right font-size-7 mt-6"
                    >
                        <v-btn
                            size="small"
                            color="secondary"
                            variant="tonal"
                            @click.prevent="dialogSizeChart = !dialogSizeChart"
                        >
                            Size Chart
                        </v-btn>
                    </div>
                </v-window-item>
            </v-window>
        </template>

        <ResponsiveModal
            v-if="product.garmentSizes?.length"
            v-model="dialogSizeChart"
            :persistent="false"
            width="60%"
            mobile-width="80%"
        >
            <template #default="{ onExit, openCount }">
                <GarmentSizeChart
                    :key="`address-form-open-${openCount}`"
                    :sizes="product.garmentSizes"
                    :in-modal="true"
                    @on-exit="onExit()"
                ></GarmentSizeChart>
            </template>
        </ResponsiveModal>
    </div>
</template>

<script lang="ts" setup>
    import type {
        Product,
        UserBody,
        CartGuestBodyResponseData
    } from '~/types';
    import { defaultGarmentGuestBody, garmentGuestBodyModel } from '~/models';
    import { createReusableTemplate } from '@vueuse/core';
    import { set as lo_set } from 'es-toolkit/compat';
    import { useLocalState } from '~/composables';
    import * as v from 'valibot';
    import DividerWithText from '~/components/elements/DividerWithText/DividerWithText.vue';
    import GarmentSizeOptionInput from '~/components/controls/GarmentSizeOptionInput/GarmentSizeOptionInput.vue';
    import GarmentGuestCustomOptions from '~/components/products/apparel/GarmentGuestCustomOptions/GarmentGuestCustomOptions.vue';
    import GarmentBodySelectorMenuInput from '~/components/controls/GarmentBodySelectorMenuInput/GarmentBodySelectorMenuInput.vue';
    import GarmentSizeChart from '~/components/products/apparel/GarmentSizeChart/GarmentSizeChart.vue';
    import SignInPrompt from '~/components/user/SignInPrompt/SignInPrompt.vue';


    const props = withDefaults(
        defineProps<{
            body?: Nullable<string | CartGuestBodyResponseData>;
            guestBodyObj?: Nullable<CartGuestBodyResponseData>;
            size?: Nullable<string>;
            product: Product;
        }>(),
        {
            body: null,
            size: null
        }
    );

    const emit = defineEmits<{
        'update:body': [value: Nullable<string>];
        'update:guest-body-obj': [value: Nullable<CartGuestBodyResponseData>];
        'update:size': [value: Nullable<string>];
    }>();

    const nuxtApp = useNuxtApp();
    const { isLoggedIn } = nuxtApp.$user;
    const GarmentCustomOptions = createReusableTemplate();
    const GarmentSizedOptions = createReusableTemplate();
    const localStateObj = useLocalState();
    const bodyModel = ref<Nullable<UserBody>>(null);
    const sizeModel = ref<Nullable<string>>(getSavedFixedSizeUid());
    const hasSizes = computed<boolean>(() => !!props.product?.garmentSizes?.length);
    const garmentGuestOptsModel = ref<Nullable<CartGuestBodyResponseData> | NullableDeep<CartGuestBodyResponseData>>(getGuestBody());

    const tabs = ref<number>(0);
    const dialogSizeChart = ref(false);

    const unWatchModels = watch(
        [
            bodyModel,
            sizeModel,
            garmentGuestOptsModel,
            tabs
        ],
        (
            [
                newValBody,
                newValSize,
                newGuestBody,
                newValTabs
            ]
            /* , [oldValBody, oldValSize, oldGuestBody, oldValTabs] */
        ) => {
            let body = newValBody?.uid ?? null;
            let guestBodyObj = null;
            let size = newValSize ?? null;

            if (!isLoggedIn.value && newGuestBody) {
                const parsed = v.safeParse(garmentGuestBodyModel, newGuestBody);

                if (parsed.success) {
                    guestBodyObj = newGuestBody as CartGuestBodyResponseData;
                }
            }

            if (size) {
                localStateObj.value.productGarmentFixedSize = size;
            }

            if (hasSizes.value) {
                switch (tabs.value) {
                    case 0:
                        size = null;
                        break;
                    case 1:
                        body = null;
                        guestBodyObj = null;
                        break;
                }
            }

            emit('update:body', body);
            emit('update:guest-body-obj', guestBodyObj);
            emit('update:size', size);
        },
        { immediate: true }
    );

    function getSavedFixedSizeUid(): Nullable<string> {
        const { productGarmentFixedSize } = localStateObj.value;
        const sizes = props.product?.garmentSizes;

        if (productGarmentFixedSize && sizes) {
            const found = sizes.find((s) => s.uid === productGarmentFixedSize);

            if (found) {
                return productGarmentFixedSize;
            }

            localStateObj.value.productGarmentFixedSize = null;
        }

        return null;
    }

    function getGuestBody(): CartGuestBodyResponseData | NullableDeep<CartGuestBodyResponseData> {
        const gender = getGender();
        const obj = localStateObj.value?.productGarmentGuestBody?.[gender];

        if (obj) {
            return obj;
        }

        return Object.assign({}, defaultGarmentGuestBody, {
            gender: (gender === 'womens') ? 'F' : 'M'
        });
    }

    function onGuestBodyChange(guestBody: Nullable<CartGuestBodyResponseData>): void {
        lo_set(localStateObj.value, ['productGarmentGuestBody', getGender()], guestBody);
    }

    // TODO: This should support unisex/other etc.
    function getGender(): string {
        let { gender } = props.product;
        return gender === 'womens' ? gender : 'mens';
    }

    onBeforeUnmount(() => {
        unWatchModels();
    });
</script>

<style lang="scss"></style>
